import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Alert,
  Grid,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import Stack from '@mui/system/Stack'
import Button from '@mui/material/Button'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAPI } from '../../api'
import MasterSelect from './MasterSelect'
import { useTranslation } from 'react-i18next'
import {
  textColumn,
  keyColumn,
  Column,
  floatColumn,
  createAddRowsComponent,
  checkboxColumn,
  DynamicDataSheetGrid,
} from 'react-datasheet-grid'
import { FormProvider, useForm } from 'react-hook-form'
import i18n from '../../i18n'
import { useSnackbar } from 'notistack'
import { Filter, InvoiceRow, SheetError } from './types'
import productSelectColumn from './productColumn'
import { isEmpty } from 'lodash'
import './index.css'
import useWindowDimensions from '../../customHook/useWindowDimensions'
import { TemlateListSearch } from '../PrintScreen/PrintList/TemplateListSearch'
import usePrintStore from '../../store/printStore'
import { PrintTemplate, PrintTemplateDetail } from '../OCRImport/types'
import { sortPrintTemplateDetails } from '../../utils/sortUtils'
import { ContextMenu } from '../OCRImport/contextMenu'
import { sumOfTotalCost } from '../../utils/sumOfToalCost'
import DataExportButton from '../reusableComponents/DataExportButton'
import { Product } from '../../api/types'

export const FORM_DIV_HEIGHT = 200
export const EXTRA_BOTTOM_MARGIN = 200
export const DATAGRID_ROW_HEIGHT = 35
const filterSchema = yup.object().shape(
  {
    consignor: yup.string().when('shipper', {
      is: (shipper: string) => !shipper,
      then: () =>
        yup.string().required(
          i18n.t('validations.atLeastOne', {
            field: `${i18n.t('invoices.filters.consignor')}/${i18n.t(
              'invoices.filters.shipper'
            )}`,
          })
        ),
    }),
    shipper: yup.string().when('consignor', {
      is: (consignor: string) => !consignor,
      then: () =>
        yup.string().required(
          i18n.t('validations.atLeastOne', {
            field: `${i18n.t('invoices.filters.consignor')}/${i18n.t(
              'invoices.filters.shipper'
            )}`,
          })
        ),
    }),
  },
  [['consignor', 'shipper']]
)

const sheetSchema = yup
  .array()
  .of(
    yup.object().shape({
      checked: yup.boolean().default(false),
      product: yup
        .mixed()
        .test((value) => typeof value === 'string' || typeof value === 'number')
        .required(
          i18n.t('validations.required', {
            field: i18n.t('invoices.fields.product'),
          })
        ),
      quantity: yup.number().required(
        i18n.t('validations.required', {
          field: i18n.t('invoices.fields.quantity'),
        })
      ),
      unit_price: yup.number().required(
        i18n.t('validations.required', {
          field: i18n.t('invoices.fields.unit_price'),
        })
      ),
      total_price: yup.number().required(
        i18n.t('validations.required', {
          field: i18n.t('invoices.fields.total_price'),
        })
      ),
      material: yup.string().when('checked', {
        is: true,
        then: () =>
          yup.string().required(
            i18n.t('validations.requiredIfSelected', {
              field: i18n.t('invoices.fields.material'),
            })
          ),
        otherwise: () => yup.string().nullable(),
      }),
      jan_code: yup.string().when('checked', {
        is: true,
        then: () =>
          yup.string().required(
            i18n.t('validations.requiredIfSelected', {
              field: i18n.t('invoices.fields.jan_code'),
            })
          ),
        otherwise: () => yup.string().nullable(),
      }),
      hs_code: yup.string().when('checked', {
        is: true,
        then: () =>
          yup.string().required(
            i18n.t('validations.requiredIfSelected', {
              field: i18n.t('invoices.fields.hs_code'),
            })
          ),
        otherwise: () => yup.string().nullable(),
      }),
    })
  )
  .required()
  // .test({
  //   name: 'noDuplicateProduct',
  //   message: i18n.t('validations.noDuplicateProduct'),
  //   test: function (value) {
  //     const products = value.map((v) => v.product).filter((v) => !isNil(v))
  //     if (products.length !== new Set(products).size) {
  //       // find duplicated product index
  //       const duplicatedIndex: number[] = []
  //       products.forEach((p, i) => {
  //         if (products.indexOf(p) !== i && duplicatedIndex.indexOf(p) === -1) {
  //           duplicatedIndex.push(i)
  //         }
  //       })
  //       return this.createError({
  //         type: 'rowError',
  //         path: `${duplicatedIndex.join(',')}`,
  //         message: i18n.t('validations.noDuplicate', {
  //           field: i18n.t('invoices.fields.product'),
  //         }),
  //       })
  //     }
  //     return true
  //   },
  // })
  .transform((value) =>
    value.map((v: InvoiceRow) => ({
      ...v,
      product: v.product
        ? v.product.id !== undefined
          ? v.product.id
          : v.product.product_name
        : null,
    }))
  )

export default function Invoice() {
  const api = useAPI()
  const { t } = useTranslation()
  const [filter, setFilter] = useState<Filter | null>(null)
  const [errors, setErrors] = useState<SheetError[]>([])
  const [templateSelected, setTemplateSelected] = useState(false)
  const [totalAmountSum, setTotalAmountSum] = useState<string | number>('')
  const [format, setFormat] = useState<string>('En')
  const { products, printTemplateId } = usePrintStore()
  const [template, setTemplate] = useState<PrintTemplate | null>(null)
  const cellClassName = useCallback(
    ({
      columnId,
      rowIndex,
    }: {
      rowData: InvoiceRow
      rowIndex: number
      columnId?: string | undefined
    }) =>
      errors.some((e) => e.row === rowIndex && e.column === columnId)
        ? 'cell-error'
        : undefined,
    [errors]
  )

  const rowClassName = useCallback(
    ({ rowIndex }: { rowIndex: number }) =>
      errors.some((e) => e.row === rowIndex && !e.column)
        ? 'row-error'
        : undefined,
    [errors]
  )
  const handleFormatChange = (e: SelectChangeEvent) => {
    setFormat(e.target.value as string)
  }

  const numberColumn = useMemo(
    () =>
      ({
        ...textColumn,
        valueViewer: ({ cellData }: { cellData: number | null }) => {
          return typeof cellData === 'number' ? cellData.toFixed(10) : cellData
        },
        valueEditor: ({
          cellData,
          onChange,
        }: {
          cellData: number | null
          onChange: (data: number | null) => void
        }) => {
          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = parseFloat(e.target.value)
            onChange(isNaN(newValue) ? null : newValue)
          }

          return (
            <input
              type="number"
              step="any"
              value={cellData ?? ''}
              onChange={handleChange}
            />
          )
        },
      }) as Partial<Column<number | null, any, string>>,
    []
  )

  const columns: Column<InvoiceRow>[] = useMemo(
    () => [
      {
        ...keyColumn('product_no', textColumn),
        title: t('invoices.fields.product_no'),
        basis: 120,
        cellClassName,
      },
      {
        ...keyColumn('product', productSelectColumn({ filter: filter! })),
        title: t('invoices.fields.product'),
        basis: 280,
        cellClassName,
      },
      {
        ...keyColumn('unit_price', numberColumn),
        title: t('invoices.fields.unit_price'),
        basis: 140,
        cellClassName,
      },
      {
        ...keyColumn('quantity', numberColumn),
        title: t('invoices.fields.quantity'),
        basis: 140,
        cellClassName,
      },
      {
        ...keyColumn('total_price', numberColumn),
        title: `${t('invoices.fields.total_price')} ${totalAmountSum}`,
        basis: 140,
        cellClassName,
      },
      {
        ...keyColumn('material', textColumn),
        title: t('invoices.fields.material'),
        basis: 140,
        cellClassName,
      },
      {
        ...keyColumn('jan_code', textColumn),
        title: t('invoices.fields.jan_code'),
        basis: 140,
        cellClassName,
      },
      {
        ...keyColumn('hs_code', textColumn),
        title: t('invoices.fields.hs_code'),
        basis: 210,
        cellClassName,
      },
      {
        ...keyColumn('checked', checkboxColumn),
        title: t('invoices.fields.checked'),
        basis: 70,
        grow: 0,
        shrink: 0,
        cellClassName,
      },
    ],
    [t, filter, cellClassName, totalAmountSum, numberColumn]
  )

  const AddRows = useMemo(
    () =>
      createAddRowsComponent({
        button: t('actions.add'),
        unit: t('preferences.units.row'),
      }),
    [t]
  )

  useEffect(() => {
    if (filter && templateSelected && template) {
      const sortedData = sortPrintTemplateDetails(
        products,
        template,
        printTemplateId
      )
      const updatedItems: InvoiceRow[] = sortedData.print_template_detail.map(
        (item: PrintTemplateDetail) => ({
          checked: false,
          material: item.product?.material ?? null,
          jan_code: item.product?.jan_code ?? null,
          hs_code: item.product?.hs_code ?? null,
          product_no: item.product?.product_no ?? null,
          product: item.product ?? null,
          quantity: item.quantity ? +item.quantity : null,
          unit_price: item.unit_price ? +item.unit_price : null,
          total_price: item.total_price ? +item.total_price : null,
        })
      )
      setData(updatedItems)
      setTotalAmountSum(sumOfTotalCost(updatedItems))
    }
  }, [filter, products, templateSelected, template, printTemplateId])

  const handleCellChange = (values: InvoiceRow[]) => {
    const updatedData = values.map((item, index) => {
      const previousItem = previousData ? previousData[index] : null

      // Check if the "product" and "hs_code" field has changed
      const productChanged =
        previousItem && item.product !== previousItem.product

      const updatedItem = {
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price:
          item.unit_price && item.quantity
            ? parseFloat((item.unit_price * item.quantity).toFixed(10))
            : item.total_price
            ? item.total_price
            : null,
        checked: item.checked,
        product_no: item.product_no,
        product: item.product,
        // Set "hs_code", material, jan_code only if the "product" field has changed
        material: productChanged
          ? (item.product?.material as string)
          : item.material ?? null,
        jan_code: productChanged
          ? (item.product?.jan_code as string)
          : item.jan_code ?? null,
        hs_code: productChanged
          ? (item.product?.hs_code as string)
          : item.hs_code ?? null,
      }
      return updatedItem
    })
    setTotalAmountSum(sumOfTotalCost(updatedData))
    setData(updatedData)
    setErrors([])

    // Update the previousData for the next iteration
    setPreviousData([...values])
  }

  const { enqueueSnackbar } = useSnackbar()
  const initialRows: InvoiceRow[] = Array.from({ length: 20 }, () => ({
    checked: false,
    product_no: null,
    product: null,
    quantity: null,
    unit_price: null,
    total_price: null,
    material: null,
    jan_code: null,
    hs_code: null,
  }))

  // this function takes value from child component TemplateListSearch when the onChangeIs triggered
  const templateSelect = useCallback(
    (isTemplateSelected: boolean, template: PrintTemplate | null) => {
      setTemplateSelected(isTemplateSelected)
      setTemplate(template)
    },
    []
  )
  const [data, setData] = useState<InvoiceRow[]>(initialRows)
  const [previousData, setPreviousData] = useState<InvoiceRow[]>(initialRows)
  const form = useForm<Filter>({
    resolver: yupResolver(filterSchema) as any,
    defaultValues: {
      consignor: '',
      shipper: '',
    },
  })

  const onSubmit = form.handleSubmit((data) => {
    setFilter(data)
  })

  const reset = () => {
    form.reset()
    setData(initialRows)
    setPreviousData(initialRows)
    setErrors([])
    setFilter(null)
    setTemplateSelected(false)
    setTotalAmountSum('')
  }
  const { height } = useWindowDimensions()

  const handleResolveProductCodes = async () => {
    // 処理対象の行（コードはあるが、商品データ未解決の行）を探す
    const targetIndices = data.reduce<number[]>((indices, row, index) => {
      if (row.product_no && (!row.product || row.product.product_no !== row.product_no)) {
        indices.push(index);
      }
      return indices;
    }, []);

    const newData = [...data];
    const changes = await Promise.all(
      targetIndices.map(async (index) => {
        const code = data[index].product_no;

        try {
          const params = {
            ...filter,
            search: code,
            page_size: 1,
          };

          const res = await api.getList<Partial<Product>>('invoice', params);
          const foundProduct = res.results?.[0];

          if (foundProduct) {
            newData[index] = {
              ...newData[index],
              product: foundProduct,
              material: foundProduct.material ?? null,
              jan_code: foundProduct.jan_code ?? null,
              hs_code: foundProduct.hs_code ?? null,
            };
            return true;
          }
        } catch (error) {
          console.error(error);
        }
        return false;
      })
    );

    if (changes.some((c) => c)) {
      setData(newData);
      setTotalAmountSum(sumOfTotalCost(newData));
      setErrors([]);
    }
  }


  return (
    <Stack spacing={4}>
      <Typography variant="h6">{t('invoices.title')}</Typography>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={5}>
              <MasterSelect
                resource="consignors"
                getLabel={(c) => `${c.japanese_name} / ${c.english_name}`}
                name="consignor"
                width={600}
                height={FORM_DIV_HEIGHT}
              />
            </Grid>
            {/* <Grid item xs={5}>
              <MasterSelect
                resource="shippers"
                getLabel={(s) => s.name
                name="shipper"
                width={600}
                height={FORM_DIV_HEIGHT}
              />
            </Grid> */}
            <Grid item xs={1} display="flex">
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={form.formState.isSubmitSuccessful}
              >
                {t('actions.inputStart')}
              </Button>
            </Grid>
            <Grid item xs={1} display="flex">
              <Button
                fullWidth
                variant="contained"
                onClick={reset}
                disabled={!form.formState.isSubmitSuccessful}
              >
                {t('actions.clear')}
              </Button>
            </Grid>
            {form.formState.isSubmitSuccessful && (
              <Grid item xs={3} sx={{ ml: 'auto' }} display="flex">
                <TemlateListSearch
                  isPrintScreen={false}
                  filter={filter}
                  onChangeIndicator={templateSelect}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
      {form.formState.isSubmitSuccessful && (
        <>
          <DynamicDataSheetGrid
            value={data}
            columns={columns}
            onChange={handleCellChange}
            addRowsComponent={AddRows as any}
            contextMenuComponent={(props) => <ContextMenu {...props} />}
            rowHeight={DATAGRID_ROW_HEIGHT}
            rowClassName={rowClassName}
            height={height - FORM_DIV_HEIGHT - EXTRA_BOTTOM_MARGIN}
          />
          {!isEmpty(errors) && (
            <Stack>
              {errors.map((e) => (
                <Alert severity="error">
                  <span>{e.row + 1}行目：</span>
                  {e.message}
                </Alert>
              ))}
            </Stack>
          )}
          <Grid container spacing={1} alignItems="stretch">
            <Grid item xs={2}>
              <Button
                variant="contained"
                onClick={handleResolveProductCodes}
                sx={{ height: '100%'}}
              >
                {t('actions.importProductCodes')}
              </Button>
            </Grid>

            <Grid item xs display="flex" justifyContent="flex-end">
              <Stack flexDirection="row-reverse" gap={1}>
                <DataExportButton
                  data={data}
                  filter={filter}
                  fileName={template?.name || 'DefaultTemplateName'}
                  format={format}
                  handleFormatChange={handleFormatChange}
                  setErrors={setErrors}
                />
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  )
}
